
import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { Modal, Form, Spinner } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import CommonButton from "../../components/commonButton";
import InputField from "../../components/form/InputField";
import { fetchActiveCities, fetchActiveStates } from "../../redux/slice/locationSlice";
import { allUsersList } from "../../redux/slice/allUsersSlice";
import {
    createUserListing,
    updateUserListing,
    uploadMarketplaceMedia,
    deleteMarketplaceMedia,
    fetchMarketplaceCategories,
    fetchListingDetails,
    clearListingDetails,
    fetchMarketplaceConfig
} from "../../redux/slice/marketplaceSlice";
import "./index.css";
import withModal from "../../components/withModal";
import { useToast } from "../../context/TostContext";
import { FormLabel } from "../../components/form/utils";
import CustomSelect from "../../components/form/CustomSelect";

function MarketplaceUserListingModal({ onClose, editData = null, onSuccess, isView = false, userId }) {
    const dispatch = useDispatch();
    const fileInputRef = useRef(null);
    const { details, config, categories = [] } = useSelector((state) => state.marketplace);
    const { states = [], cities = [] } = useSelector((state) => state.location || {});
    const toast = useToast();
    useEffect(() => {
        if (!config || !config.listing_types) {
            dispatch(fetchMarketplaceConfig());
        }
        dispatch(fetchActiveStates({ limit: 20 }));
        dispatch(fetchMarketplaceCategories());
    }, [config, dispatch]);

    const formatOptions = (arr) => {
        if (!arr || !Array.isArray(arr)) return [];
        return arr.map(item => {
            const label = typeof item === 'string'
                ? item.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
                : item;
            return { value: item, label };
        });
    };

    const fetchListingTypeOptions = useCallback(async (search = "") => {
        const options = formatOptions(config?.listing_types);
        const filtered = options.filter(opt => opt.label.toLowerCase().includes(search.toLowerCase()));
        return { data: filtered.map(opt => ({ id: opt.value, name: opt.label })), meta: { page: 1, pages: 1 } };
    }, [config?.listing_types]);

    const fetchPriceUnitOptions = useCallback(async (search = "") => {
        const options = formatOptions(config?.price_units);
        const filtered = options.filter(opt => opt.label.toLowerCase().includes(search.toLowerCase()));
        return { data: filtered.map(opt => ({ id: opt.value, name: opt.label })), meta: { page: 1, pages: 1 } };
    }, [config?.price_units]);

    const fetchConditionOptions = useCallback(async (search = "") => {
        const options = formatOptions(config?.conditions);
        const filtered = options.filter(opt => opt.label.toLowerCase().includes(search.toLowerCase()));
        return { data: filtered.map(opt => ({ id: opt.value, name: opt.label })), meta: { page: 1, pages: 1 } };
    }, [config?.conditions]);

    const [formData, setFormData] = useState({
        user_id: "",
        category_id: "",
        listing_type: "",
        price_unit: "",
        name: "",
        price: "",
        mrp: "",
        condition: "",
        state_id: "",
        city_id: "",
        brief_description: "",
        photos: [],
        discount: 0
    });

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isUploading, setIsUploading] = useState(false);

    const stateSearchTimeoutRef = useRef(null);
    const citySearchTimeoutRef = useRef(null);

    const handleStateSearch = (inputValue, { action }) => {
        if (action === "input-change") {
            if (stateSearchTimeoutRef.current) clearTimeout(stateSearchTimeoutRef.current);
            stateSearchTimeoutRef.current = setTimeout(() => {
                dispatch(fetchActiveStates({ search: inputValue, limit: 20 }));
            }, 400);
        }
    };

    const handleCitySearch = (inputValue, { action }) => {
        if (action === "input-change") {
            if (citySearchTimeoutRef.current) clearTimeout(citySearchTimeoutRef.current);
            citySearchTimeoutRef.current = setTimeout(() => {
                dispatch(fetchActiveCities({
                    search: inputValue,
                    limit: 20,
                    state_ids: formData.state_id ? [formData.state_id].join(",") : undefined
                }));
            }, 400);
        }
    };

    useEffect(() => {
        if (formData.state_id) {
            dispatch(fetchActiveCities({ limit: 20, state_ids: formData.state_id }));
        }
    }, [formData.state_id, dispatch]);

    const stateOptions = useMemo(() => {
        const fetchedOptions = states.map((s) => ({ value: s.id, label: s.name }));
        const additionalOptions = [];
        const currentListingState = details?.state;
        if (currentListingState && currentListingState.id && !states.find(s => s.id === currentListingState.id)) {
            additionalOptions.push({ value: currentListingState.id, label: currentListingState.name });
        }
        const allOptions = [...additionalOptions, ...fetchedOptions];
        const uniqueOptions = Array.from(new Map(allOptions.map(item => [item.value, item])).values());
        return uniqueOptions;
    }, [states, details]);

    const cityOptions = useMemo(() => {
        const fetchedOptions = cities.map((c) => ({ value: c.id, label: c.name }));
        const additionalOptions = [];
        const currentListingCity = details?.city;
        if (currentListingCity && currentListingCity.id && !cities.find(c => c.id === currentListingCity.id)) {
            additionalOptions.push({ value: currentListingCity.id, label: currentListingCity.name });
        }
        const allOptions = [...additionalOptions, ...fetchedOptions];
        const uniqueOptions = Array.from(new Map(allOptions.map(item => [item.value, item])).values());
        return uniqueOptions;
    }, [cities, details]);

    const categoryOptions = useMemo(() => {
        const fetchedOptions = categories.map((parent) => ({
            label: parent.name,
            options: (parent.subcategories || []).map((sub) => ({
                value: sub.id,
                label: sub.name,
            })),
        }));
        const currentListingCategory = details?.category;
        let found = false;
        if (currentListingCategory) {
            for (const parent of fetchedOptions) {
                if (parent.options.find(sub => sub.value === currentListingCategory.id)) {
                    found = true;
                    break;
                }
            }
        }
        if (currentListingCategory && !found) {
            const parentName = details?.category?.parent?.name || "Other";
            const existingParent = fetchedOptions.find(p => p.label === parentName);
            if (existingParent) {
                existingParent.options.push({ value: currentListingCategory.id, label: currentListingCategory.name });
            } else {
                fetchedOptions.push({
                    label: parentName,
                    options: [{ value: currentListingCategory.id, label: currentListingCategory.name }]
                });
            }
        }
        return fetchedOptions;
    }, [categories, details]);

    useEffect(() => {
        if (editData) {
            dispatch(fetchListingDetails({ id: editData.id, seller_type: 'user' }));
        }
        return () => {
            dispatch(clearListingDetails());
        };
    }, [editData, dispatch]);

    useEffect(() => {
        if (details) {
            setFormData({
                user_id: details.seller_id,
                category_id: details.category_id,
                listing_type: details.user_listing_details?.listing_type || "",
                price_unit: details.user_listing_details?.price_unit || "",
                name: details.name,
                price: details.user_listing_details?.price || "",
                mrp: details.user_listing_details?.mrp || "",
                condition: details.user_listing_details?.condition || "",
                state_id: details.state_id,
                city_id: details.city_id,
                brief_description: details.brief_description || "",
                photos: details.photos?.filter(m => m.type === 'image').map(m => ({ id: m.id, url: m.url })) || [],
                discount: details.user_listing_details?.discount || 0
            });
        } else if (!editData) {
            setFormData({
                user_id: userId || "",
                category_id: "",
                listing_type: "",
                price_unit: "",
                name: "",
                price: "",
                mrp: "",
                condition: "",
                state_id: "",
                city_id: "",
                brief_description: "",
                photos: [],
                discount: 0
            });
        }
    }, [details, editData, userId]);

    useEffect(() => {
        setFormData(prev => {
            let updated = { ...prev };
            let changed = false;

            if (!updated.listing_type && config?.listing_types?.length > 0) {
                updated.listing_type = config.listing_types[0];
                changed = true;
            }

            if (!updated.condition && config?.conditions?.length > 0) {
                updated.condition = config.conditions[0];
                changed = true;
            }

            if (["rent", "service"].includes(updated.listing_type) && !updated.price_unit && config?.price_units?.length > 0) {
                updated.price_unit = config.price_units[0];
                changed = true;
            }

            return changed ? updated : prev;
        });
    }, [formData.listing_type, formData.price_unit, formData.condition, config?.listing_types, config?.conditions, config?.price_units]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => {
            const updated = { ...prev, [name]: value };
            if (name === "price" || name === "mrp") {
                const priceNum = parseFloat(updated.price) || 0;
                const mrpNum = parseFloat(updated.mrp) || 0;
                if (mrpNum > 0 && mrpNum > priceNum) {
                    updated.discount = ((mrpNum - priceNum) / mrpNum * 100).toFixed(2);
                } else {
                    updated.discount = 0;
                }
            }
            return updated;
        });
    };

    const handleSelectChange = (name, value) => {
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const fetchUserOptionsPaginated = useCallback(async (search, page) => {
        try {
            const result = await dispatch(allUsersList({ search, page, limit: 10 })).unwrap();
            return {
                data: result.data.data,
                meta: result.data.pagination || result.data.meta || {}
            };
        } catch (error) {
            return { data: [], meta: { page: 1, pages: 1 } };
        }
    }, [dispatch]);

    const userDataMapper = useCallback((data) => {
        return (data || []).map(user => ({
            value: user.id,
            label: `${user.first_name || ""} ${user.last_name || ""} (${user.mobile_number || ""})`.trim()
        }));
    }, []);

    const initialUserLabel = useMemo(() => {
        if (details?.seller) {
            const { first_name, last_name, mobile_number } = details.seller;
            return `${first_name || ""} ${last_name || ""} (${mobile_number || ""})`.trim();
        }
        return "";
    }, [details]);



    const handleFileChange = async (e) => {
        const files = Array.from(e.target.files);
        if (files.length === 0) return;

        if (!formData.user_id) {
            toast.error("Please select a user first");
            return;
        }

        if (formData.photos.length + files.length > 5) {
            toast.error("You can upload maximum 5 photos");
            return;
        }

        setIsUploading(true);
        try {
            const result = await dispatch(uploadMarketplaceMedia({
                user_id: formData.user_id,
                files: files,
                collection: "main"
            })).unwrap();

            const newPhotos = result.data.map(m => ({ id: m.id, url: m.url }));
            setFormData(prev => ({
                ...prev,
                photos: [...prev.photos, ...newPhotos]
            }));
            toast.success("Photos uploaded successfully");
        } catch (error) {
            toast.error(error || "Upload failed");
        } finally {
            setIsUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = "";
        }
    };

    const handleRemovePhoto = async (photoId) => {
        try {
            await dispatch(deleteMarketplaceMedia(photoId)).unwrap();
            setFormData(prev => ({
                ...prev,
                photos: prev.photos.filter(p => p.id !== photoId)
            }));
        } catch (error) {
            // Even if delete fails on server, remove from local state
            setFormData(prev => ({
                ...prev,
                photos: prev.photos.filter(p => p.id !== photoId)
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            if (!editData && !userId && !formData.user_id) {
                toast.error("Please select a user");
                return;
            }
            if (!formData.category_id) {
                toast.error("Please select a product category");
                return;
            }
            if (!formData.name || !formData.name.trim()) {
                toast.error("Please enter a product name");
                return;
            }

            const priceVal = parseFloat(formData.price);
            if (!formData.price || isNaN(priceVal) || priceVal <= 0) {
                toast.error("Please enter the price");
                return;
            }

            const mrpVal = parseFloat(formData.mrp);
            if (formData.listing_type === 'sell' && (!formData.mrp || isNaN(mrpVal) || mrpVal <= 0)) {
                toast.error("Please enter MRP for sell listing");
                return;
            }

            if (formData.listing_type === 'sell' && !isNaN(mrpVal) && mrpVal > 0 && priceVal > mrpVal) {
                toast.error("Price must be less than or equal to MRP");
                return;
            }

            if (!formData.condition) {
                toast.error("Please select a condition");
                return;
            }
            if (!formData.state_id) {
                toast.error("Please select a state");
                return;
            }
            if (!formData.city_id) {
                toast.error("Please select a city");
                return;
            }
            if (!formData.photos || formData.photos.length === 0) {
                toast.error("Please upload at least one photo");
                return;
            }

            setIsSubmitting(true);

            const payload = {
                ...formData,
                price: priceVal,
                mrp: formData.listing_type === 'sell' ? mrpVal : 0,
                photos: formData.photos.map(p => ({ id: p.id }))
            };
            delete payload.discount;

            if (editData) {
                // Remove user_id from update as it's not allowed/needed
                delete payload.user_id;
                await dispatch(updateUserListing({ id: editData.id, data: payload })).unwrap();
            } else {
                await dispatch(createUserListing(payload)).unwrap();
            }

            toast.success(`Listing ${editData ? 'updated' : 'created'} successfully`);
            onSuccess();
            onClose();
        } catch (error) {
            console.error("Submit error:", error);
            toast.error(error?.message || "Something went wrong");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Form onSubmit={handleSubmit} noValidate>
            <Modal.Body className="grid gap-4">

                {!editData && !userId && (

                    <CustomSelect
                        className="g-col-12"
                        label={{ name: "User" }}
                        name="user_id"
                        required
                        isClearable
                        value={formData.user_id}
                        onChange={handleSelectChange}
                        fetchOptions={fetchUserOptionsPaginated}
                        dataMapper={userDataMapper}
                        placeholder="Search & Select User (Mobile Number or Name)"
                        initialLabel={initialUserLabel}
                        isDisabled={isView}
                    />

                )}


                <CustomSelect
                    className="g-col-6"
                    label={{ name: "Listing Type" }}
                    name="listing_type"
                    value={formData.listing_type}
                    fetchOptions={fetchListingTypeOptions}
                    onChange={(name, val) => {
                        handleSelectChange("listing_type", val);
                        if (val === 'sell') {
                            handleSelectChange("price_unit", "");
                        }
                    }}
                    placeholder="Select..."
                    required
                    isDisabled={isView}
                    isClearable={false}
                />

                <InputField
                    className="g-col-6"
                    label={{ name: "Product/Service Name" }}
                    required
                    input={{
                        name: "name",
                        value: formData.name,
                        onChange: handleInputChange,
                        placeholder: "E.g. Stainless Steel Pipes",
                        disabled: isView
                    }}
                />

                <CustomSelect
                    className="g-col-6"
                    label={{ name: "Product Category" }}
                    options={categoryOptions}
                    value={categoryOptions.reduce((acc, parent) => {
                        const found = parent.options.find(opt => opt.value === formData.category_id);
                        if (found) return found;
                        return acc;
                    }, null)}
                    onChange={(selectedOption) => {
                        handleSelectChange("category_id", selectedOption ? selectedOption.value : "");
                    }}
                    placeholder="Select Category"
                    isClearable
                    isDisabled={isView}


                />

                {formData.listing_type === 'sell' && (

                    <CustomSelect
                        label={{ name: "Condition" }}
                        className="g-col-6"
                        name="condition"
                        value={formData.condition}
                        fetchOptions={fetchConditionOptions}
                        onChange={(name, val) => handleSelectChange("condition", val)}
                        placeholder="Select..."
                        required
                        isDisabled={isView}
                        isClearable={false}
                    />
                )}

                {formData.listing_type === 'sell' && (
                    <InputField
                        className="g-col-3"
                        label={{ name: "MRP (₹)" }}
                        required
                        input={{
                            type: "number",
                            name: "mrp",
                            value: formData.mrp,
                            onChange: handleInputChange,
                            placeholder: "0.00",
                            disabled: isView
                        }}
                    />
                )}

                {["rent", "service"].includes(formData.listing_type) ? (
                    <div className="g-col-6">
                        <label className="form-label fw-semibold">Price Per Unit <span className="text-danger">*</span></label>
                        <div className="d-flex gap-2">
                            <div style={{ flex: 1 }}>
                                <InputField
                                    label={{ name: "Price Per Unit", isHidden: true }}
                                    required
                                    input={{
                                        type: "number",
                                        name: "price",
                                        value: formData.price,
                                        onChange: handleInputChange,
                                        placeholder: "Enter price per unit",
                                        disabled: isView
                                    }}
                                />
                            </div>

                            <CustomSelect
                                name="price_unit"
                                value={formData.price_unit}
                                fetchOptions={fetchPriceUnitOptions}
                                onChange={(name, val) => handleSelectChange("price_unit", val)}
                                placeholder="Select..."
                                required
                                label={{ name: "Price Unit", isHidden: true }}
                                isDisabled={isView}
                                isClearable={false}
                                style={{ width: '130px' }}
                            />

                        </div>
                    </div>
                ) : (
                    <InputField
                        className="g-col-3"
                        label={{ name: "Price (₹)" }}
                        required
                        input={{
                            type: "number",
                            name: "price",
                            value: formData.price,
                            onChange: handleInputChange,
                            placeholder: "0.00",
                            disabled: isView
                        }}
                    />
                )}

                {formData.listing_type === 'sell' && (
                    <InputField
                        className="g-col-3"
                        label={{ name: "Discount (%)" }}
                        input={{
                            type: "text",
                            name: "discount",
                            value: formData.discount && formData.discount !== "0.00" ? `${formData.discount}%` : "0%",
                            disabled: true,
                            readOnly: true,
                            placeholder: "0%"
                        }}
                    />
                )}


                <CustomSelect
                    className="g-col-6"
                    label={{ name: "State" }}
                    required
                    options={stateOptions}
                    value={stateOptions.find(opt => opt.value === formData.state_id) || null}
                    onChange={(selectedOption) => {
                        handleSelectChange("state_id", selectedOption ? selectedOption.value : "");
                        handleSelectChange("city_id", "");
                    }}
                    onInputChange={handleStateSearch}
                    onMenuOpen={() => {
                        dispatch(fetchActiveStates({ limit: 20 }));
                    }}
                    placeholder="Search State"
                    isClearable
                    isDisabled={isView}


                />


                <CustomSelect
                    className="g-col-6"
                    label={{ name: "City" }}
                    required
                    options={cityOptions}
                    value={cityOptions.find(opt => opt.value === formData.city_id) || null}
                    onChange={(selectedOption) => {
                        handleSelectChange("city_id", selectedOption ? selectedOption.value : "");
                    }}
                    onInputChange={handleCitySearch}
                    onMenuOpen={() => {
                        if (formData.state_id) {
                            dispatch(fetchActiveCities({ limit: 20, state_ids: formData.state_id }));
                        }
                    }}
                    placeholder="Search City"
                    isClearable
                    isDisabled={!formData.state_id || isView}

                />

                <InputField
                    className="g-col-12"
                    label={{ name: "Description" }}
                    input={{
                        as: "textarea",
                        rows: 2,
                        name: "brief_description",
                        value: formData.brief_description,
                        onChange: handleInputChange,
                        placeholder: "Tell more about the item...",
                        disabled: isView
                    }}
                />

                <div className="g-col-12">
                    <FormLabel labelName="Product Photos (1-5)" required={true} />
                    <div className="marketplace-photo-container mt-2">
                        {formData.photos.map((photo, index) => (
                            <div key={photo.id || index} className="photo-card">
                                <div className="photo-preview-wrapper">
                                    <img src={photo.url} alt={`Listing row ${index + 1}`} />
                                    <div className="photo-uploaded-tick">
                                        <svg width="16" height="16" viewBox="0 0 14 14" fill="none">
                                            <path d="M2.5 7l3.5 3.5 5.5-5.5" stroke="#28c76f" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    </div>
                                </div>
                                <div className="photo-card-info">
                                    <div className="photo-card-name">
                                        {photo.url?.split("/").pop() || `Photo ${index + 1}.png`}
                                    </div>
                                    <div style={{ fontSize: "10px", color: "#888", marginBottom: 6 }}>—</div>
                                </div>
                                <div className="photo-card-footer">
                                    <button
                                        type="button"
                                        className="photo-action-btn"
                                        onClick={() => !isView && handleRemovePhoto(photo.id)}
                                        disabled={isView}
                                        style={{ cursor: isView ? "default" : "pointer", opacity: isView ? 0.6 : 1 }}
                                    >
                                        Remove File
                                    </button>
                                </div>
                            </div>
                        ))}

                        {formData.photos.length < 5 && (
                            <div
                                className={`photo-card photo-add-card ${isView ? 'disabled' : ''}`}
                                onClick={() => !isUploading && !isView && fileInputRef.current?.click()}
                                style={{ opacity: (isUploading || isView) ? 0.6 : 1, cursor: (isUploading || isView) ? "not-allowed" : "pointer" }}
                            >
                                <div className="photo-add-content">
                                    {isUploading ? (
                                        <Spinner animation="border" size="sm" variant="primary" />
                                    ) : (
                                        <>
                                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="1.5">
                                                <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                            <span>Drop files here or click to upload</span>
                                        </>
                                    )}
                                </div>
                                <div className="photo-card-footer">
                                    <button type="button" className="photo-action-btn" disabled={isUploading || isView}>
                                        Add File
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                    <p className="small text-muted mt-2">
                        Please upload clear photos of your product. You can add up to 5 photos.
                    </p>

                    <input
                        type="file"
                        accept="image/*"
                        multiple
                        ref={fileInputRef}
                        className="d-none"
                        onChange={handleFileChange}
                    />
                </div>

            </Modal.Body>
            <Modal.Footer>
                <CommonButton
                    title={isView ? "Close" : "Cancel"}
                    variant="secondary"
                    onClick={onClose}
                    type="button"
                />
                {!isView && (
                    <CommonButton
                        title={editData ? "Update Listing" : "Create Listing"}
                        variant="primary"
                        type="submit"
                        isLoading={isSubmitting}
                        disabled={isUploading}
                    />
                )}
            </Modal.Footer>
        </Form>
    );
}

export default withModal(MarketplaceUserListingModal);
